import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Cart} from '../../../model/cart/Cart';
import {ChargeProfile} from '../../../model/ChargeProfile';
import {ChargeProfileService, CreateChargeProfileRequest} from '../../service-data/charge-profile-service';
import {COUNTRIES} from '../../util/countries';
import {LanguagePipe} from '../../pipe/language.pipe';

/**
 * Payment-method section of the checkout accordion.
 *
 * Extracted from `CartPageComponent`. Owns the saved-charge-profile list,
 * the inline credit-card form, the invoice toggle, and the save-as-profile
 * controls. The parent retains the readiness flags (`paymentReady`) and
 * the cross-cutting `verifyPaymentInfo()` orchestrator — this component
 * just forwards each change event up.
 *
 * Carries the `legacy-controls` host class to match `CartPageComponent`
 * per the "Modern controls" convention in `AGENTS.md`.
 */
@Component({
  selector: 'app-cart-payment-section',
  templateUrl: './cart-payment-section.component.html',
  styleUrls: ['../cart-page/checkout-shared.css', './cart-payment-section.component.css']
})
export class CartPaymentSectionComponent implements OnInit, OnChanges, OnDestroy {
  readonly countries = COUNTRIES;

  @Input() cart: Cart;
  @Input() addressReady: boolean = false;
  @Input() shippingReady: boolean = false;
  @Input() paymentReady: boolean = false;
  @Input() userControlledAccordion: boolean = false;

  // Two-way bindings for inputs the parent reads when deciding how to
  // submit the order (`preparePaymentThenPlaceOrder`).
  @Input() enterCreditCard: boolean = false;
  @Output() enterCreditCardChange = new EventEmitter<boolean>();

  @Input() invoice: boolean = false;
  @Output() invoiceChange = new EventEmitter<boolean>();

  @Input() billingAddressTheSameAsShipping: boolean = true;
  @Output() billingAddressTheSameAsShippingChange = new EventEmitter<boolean>();

  @Input() savePaymentInfoAsChargeProfile: boolean = false;
  @Output() savePaymentInfoAsChargeProfileChange = new EventEmitter<boolean>();

  @Input() savePaymentInfoAsChargeProfileName: string = '';
  @Output() savePaymentInfoAsChargeProfileNameChange = new EventEmitter<string>();

  @Output() userControlledAccordionChange = new EventEmitter<boolean>();
  /** Emitted after any input that may change payment readiness; the parent
   *  re-runs `verifyPaymentInfo()` so cross-section flags stay in sync. */
  @Output() paymentInfoChanged = new EventEmitter<void>();

  /**
   * State of the inline "Save charge profile" button offered inside the
   * credit-card form. Lets the user commit the card as a saved
   * ChargeProfile immediately (via `POST /chargeprofile/create`) BEFORE
   * placing the order, so they can select the freshly-saved profile from
   * the radio list for this cart AND every future cart. The existing
   * "save on place-order" path via `savePaymentInfoAsChargeProfile` +
   * `preparePaymentThenPlaceOrder` in the parent is unchanged and remains
   * supported for the user who wants to defer the save decision.
   *
   *  - `IDLE`   — button offered when the form is complete.
   *  - `SAVING` — tokenize in flight; button disabled, spinner shown.
   *  - `SAVED`  — success chip for ~3s; the credit-card form
   *               auto-collapses (via `enterCreditCardChange` emit) so
   *               the user sees the newly-saved row selected in the
   *               saved-profile radio list.
   *  - `ERROR`  — server refused (invalid card, gateway declined, etc.);
   *               inline error + Retry button.
   */
  saveProfileState: 'IDLE' | 'SAVING' | 'SAVED' | 'ERROR' = 'IDLE';
  /** Inline error surfaced under the button on `ERROR` state. */
  saveProfileErrorMessage: string = '';
  /** Timer handle for the SAVED → IDLE reset; cancelled on a fresh click. */
  private saveProfileResetHandle: any = null;

  /**
   * True once we have decided whether to auto-open the credit-card
   * form during initial mount. Set by {@link maybeAutoOpenCreditCardForm}
   * so we don't keep re-opening it every change-detection tick after
   * the user manually collapses it. Reset on `ngOnDestroy` or when
   * the input `cart` reference itself changes (new checkout session).
   */
  private autoOpenDecided: boolean = false;
  /**
   * Polling handle for the delayed auto-open attempt. When the
   * section mounts before `ChargeProfileService` has finished its
   * initial fetch, we retry via `setTimeout` on a short interval
   * until either (a) the list becomes non-empty, (b) `loadingState`
   * flips to LOADED with an empty list (fires the auto-open), or
   * (c) the section is destroyed. Cleared in `ngOnDestroy`.
   */
  private autoOpenPollHandle: any = null;

  constructor(protected chargeProfileService: ChargeProfileService) {}

  ngOnInit(): void {
    this.maybeAutoOpenCreditCardForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Re-run the auto-open check if the parent swapped in a new
    // cart reference (e.g. the user emptied and restarted the cart)
    // — the old decision may no longer apply.
    if (changes['cart'] && !changes['cart'].firstChange) {
      this.autoOpenDecided = false;
      this.maybeAutoOpenCreditCardForm();
    }
  }

  ngOnDestroy(): void {
    this.clearSaveProfileTimer();
    if (this.autoOpenPollHandle) {
      clearTimeout(this.autoOpenPollHandle);
      this.autoOpenPollHandle = null;
    }
  }

  /** Material icon name for the section header. */
  paymentSectionIcon(): string {
    if (this.paymentReady) { return 'check_circle'; }
    if (!this.addressReady || !this.shippingReady) { return 'block'; }
    return 'payment';
  }

  /** State-based css class for the section header icon. */
  paymentSectionClass(): string {
    if (this.paymentReady) { return 'section-ready'; }
    if (!this.addressReady || !this.shippingReady) { return 'section-blocked'; }
    return 'section-not-ready';
  }

  onAccordionToggle() {
    this.userControlledAccordion = true;
    this.userControlledAccordionChange.emit(true);
  }

  /** Atomically set this charge profile as the org default via the
   *  dedicated server endpoint (cart redesign S-4-2, shipped 2026-06-24).
   *  See `shared/ChargeProfile.md` for the contract. */
  makeChargeProfileDefault(chargeProfile: ChargeProfile) {
    if (!chargeProfile || !(chargeProfile.ID > 0)) { return; }
    chargeProfile['updatingState'] = 'UPDATING';
    this.chargeProfileService.setDefault(chargeProfile.ID).subscribe(
      () => { delete chargeProfile['updatingState']; },
      () => { delete chargeProfile['updatingState']; }
    );
  }

  /** Soft-delete the charge profile (state = INACTIVE). The dedicated
   *  wrapper exists for call-site clarity; it delegates to the inherited
   *  `deleteItem` per the shared contract.
   *
   *  When the deleted profile is the one currently selected on the cart,
   *  we clear `cart.chargeProfile` (falling back to another `default`
   *  profile if one remains) and fire `paymentInfoChanged` so the parent
   *  re-runs `verifyPaymentInfo()`. Without this, the removed row keeps
   *  showing as the selected radio, `Cart.isPaymentInfoReady()` keeps
   *  returning true against a dangling reference, and the Place-Order
   *  button stays enabled against a payment method that no longer
   *  exists server-side (bug 2026-07-22 pass 3). The parent
   *  `CartPageComponent` also subscribes to
   *  `chargeProfileService.getDeleteNotifier()` as belt-and-suspenders
   *  in case a deletion arrives via a push notification path.
   */
  deleteChargeProfile(chargeProfile: ChargeProfile) {
    if (chargeProfile && chargeProfile.ID > 0) {
      const wasSelected = !!this.cart
        && !!this.cart.chargeProfile
        && this.cart.chargeProfile.ID === chargeProfile.ID;
      this.chargeProfileService.deleteProfile(chargeProfile).subscribe(_o => {
        delete chargeProfile['updatingState'];
        if (wasSelected && this.cart) {
          const fallback = (this.chargeProfileService.objects || [])
            .find((p: any) => p && p.default && p.ID !== chargeProfile.ID);
          this.cart.setChargeProfile(fallback ? fallback : undefined);
        }
        // Always re-verify — even a non-selected deletion changes the
        // list the parent renders, and the parent's readiness state
        // should be a pure function of current cart state.
        this.paymentInfoChanged.emit();
      });
    }
  }

  /** Two-way handler for the billing-address-same-as-shipping toggle. */
  onBillingAddressSameToggle() {
    this.billingAddressTheSameAsShippingChange.emit(this.billingAddressTheSameAsShipping);
    this.paymentInfoChanged.emit();
  }

  /** Toggle the "save card as charge profile" preference. */
  toggleSavePaymentInfoAsChargeProfile() {
    this.savePaymentInfoAsChargeProfile = !this.savePaymentInfoAsChargeProfile;
    this.savePaymentInfoAsChargeProfileChange.emit(this.savePaymentInfoAsChargeProfile);
  }

  onSavePaymentInfoAsChargeProfileNameChange(value: string) {
    this.savePaymentInfoAsChargeProfileName = value;
    this.savePaymentInfoAsChargeProfileNameChange.emit(value);
  }

  /** Emit changes on the inline credit-card form fields so the parent
   *  re-runs `verifyPaymentInfo()`. */
  onPaymentFieldChange() {
    this.paymentInfoChanged.emit();
  }

  onEnterCreditCardToggle(value: boolean) {
    this.enterCreditCard = value;
    this.enterCreditCardChange.emit(value);
  }

  onInvoiceToggle() {
    this.invoice = !this.invoice;
    this.invoiceChange.emit(this.invoice);
    this.paymentInfoChanged.emit();
  }

  // ---- Card-vs-invoice radio group (only rendered when cart.invoiceable) ----
  //
  // The template binds `[(ngModel)]="paymentMethod"` and
  // `(change)="onPaymentMethodChange($event.value)"`. Neither member existed:
  // the radio group therefore threw during change detection and took the rest
  // of the payment section's view with it. This repo sets neither
  // `strictTemplates` nor `fullTemplateTypeCheck`, so nothing caught it at
  // build time -- see `npm run check:templates`.
  //
  // `paymentMethod` is DERIVED from `invoice` rather than being a second
  // stored field. Two independent values representing one mutually-exclusive
  // choice is exactly how they end up disagreeing -- and `invoice` is the one
  // the parent already reads in `preparePaymentThenPlaceOrder`, so it has to
  // stay authoritative.

  /** `'invoice'` or `'card'`, projected from the authoritative `invoice` flag. */
  get paymentMethod(): string {
    return this.invoice ? 'invoice' : 'card';
  }

  /** ngModel writes land here; delegates so both entry points behave alike. */
  set paymentMethod(value: string) {
    this.applyPaymentMethod(value);
  }

  /**
   * `(change)` handler. Fires in addition to the ngModel setter, which is why
   * `applyPaymentMethod` is written to be idempotent.
   */
  onPaymentMethodChange(value: string) {
    this.applyPaymentMethod(value);
  }

  private applyPaymentMethod(value: string) {
    const wantInvoice = ( value === 'invoice' );
    if ( this.invoice === wantInvoice ) {
      // Already in this state -- the second of the two events firing for one
      // click, or a no-op re-selection. Emitting again would make the parent
      // re-run verifyPaymentInfo() for nothing.
      return;
    }
    this.invoice = wantInvoice;
    this.invoiceChange.emit(this.invoice);

    if ( wantInvoice ) {
      // Collapse the card form: leaving it open next to a selected "Invoice"
      // radio invites the user to fill in a card that will not be charged.
      if ( this.enterCreditCard ) {
        this.enterCreditCard = false;
        this.enterCreditCardChange.emit(false);
      }
    } else if ( !this.enterCreditCard ) {
      // Switching to card with nothing saved would otherwise leave the user
      // on a section with no way to supply a payment method at all.
      const profiles = this.chargeProfileService.objects;
      if ( !profiles || profiles.length === 0 ) {
        this.enterCreditCard = true;
        this.enterCreditCardChange.emit(true);
      }
    }

    this.paymentInfoChanged.emit();
  }

  // ─── Saved charge-profile display helpers ──────────────────────

  /**
   * Row label for a saved ChargeProfile in the radio list.
   *
   * Rules (2026-07-09 pass):
   *  - Always show the user-chosen `name` first (that's the primary
   *    handle they picked when saving the card).
   *  - Append `· <brand> ****<last4>` when the server has those
   *    safe-display metadata fields. Older profiles saved before the
   *    `last4` / `cardBrand` roundtrip existed may lack them; those
   *    just show the bare name.
   *  - Also skip the suffix when the `name` itself already ends in
   *    the last-4 digits (either because the user typed them into
   *    the name, or because our immediate-save flow auto-appended
   *    them — see {@link buildProfileNameWithLast4}). Prevents
   *    "Business Visa 4242 · Visa ****4242" duplication.
   */
  chargeProfileRowLabel(chargeProfile: ChargeProfile): string {
    if (!chargeProfile) { return ''; }
    const name = (chargeProfile.name || '').trim();
    const last4 = (chargeProfile.last4 || '').trim();
    if (!last4) { return name; }
    // Skip the suffix if the name already carries the last-4 anywhere
    // near the end (matches "…4242", "…****4242", "…- 4242", etc.).
    if (new RegExp(`${last4}\\s*$`).test(name)) { return name; }
    const brand = (chargeProfile.cardBrand || '').trim();
    if (brand) {
      return `${name} \u00b7 ${brand} \u2022\u2022\u2022\u2022${last4}`;
    }
    return `${name} \u00b7 \u2022\u2022\u2022\u2022${last4}`;
  }

  // ─── Immediate "Save charge profile" flow ──────────────────────
  //
  // Kicks the ChargeProfileService.createChargeProfile call directly
  // from the payment section rather than waiting for the user to
  // proceed with `Place order`. On success the freshly-saved profile
  // appears in `chargeProfileService.objects` (via the service's
  // `addUpdateLocalItem`) and the credit-card form auto-collapses
  // back to the saved-profile radio list — where the new row is now
  // selectable. Selecting it there gives the user the "just saved
  // AND ready to use for this order" outcome.

  /**
   * True when the credit-card form has enough data to attempt a
   * tokenization save. Deliberately permissive — the merchant
   * gateway does the definitive validation (Luhn, exp, CVV format).
   * Requires:
   *  - `enterCreditCard` mode is on (form is visible).
   *  - Card number, exp month/year, CSC, and cardholder name are
   *    non-empty.
   *  - A billing postal code is available (either via the
   *    ship-to-billing toggle or the billing form).
   */
  get canSaveChargeProfile(): boolean {
    if (!this.enterCreditCard || !this.cart) { return false; }
    const cardNumber = (this.cart.cardNumberWithSpaces || '').split(' ').join('');
    if (cardNumber.length < 13) { return false; }
    const expMonthYear = (this.cart.expMonthYear || '').trim();
    if (expMonthYear.length < 5) { return false; }
    if (!this.cart.csc || this.cart.csc.trim().length < 3) { return false; }
    if (!this.cart.firstNamePayment || !this.cart.lastNamePayment) { return false; }
    // Billing postal code — from the shipping address when the
    // billing-same toggle is on, otherwise from the billing form.
    const postal = this.billingAddressTheSameAsShipping
        ? (this.cart.address && this.cart.address.postalcode)
        : this.cart.postalCodePayment;
    if (!postal || String(postal).trim().length === 0) { return false; }
    return true;
  }

  /**
   * Build the profile name we send on tokenize. If the user typed a
   * name into `savePaymentInfoAsChargeProfileName`, use it verbatim
   * UNLESS it doesn't already carry the card's last-4 digits, in
   * which case append ` ****<last4>` so the saved-profile radio row
   * gives the user a durable disambiguator (matching the display
   * convention in {@link chargeProfileRowLabel}). If they didn't
   * pick a name at all, derive one from the cardholder name +
   * last4 fallback.
   */
  private buildProfileNameWithLast4(cardNumberDigits: string): string {
    const last4 = cardNumberDigits.slice(-4);
    const userName = (this.savePaymentInfoAsChargeProfileName || '').trim();
    const holderName = `${this.cart.firstNamePayment || ''} ${this.cart.lastNamePayment || ''}`.trim();
    const base = userName || holderName || LanguagePipe.get('Credit card');
    if (last4 && !new RegExp(`${last4}\\s*$`).test(base)) {
      return `${base} \u2022\u2022\u2022\u2022${last4}`;
    }
    return base;
  }

  /**
   * Fire the tokenize+persist immediately from the payment section.
   * On success, wipe the raw card fields, collapse the credit-card
   * form, and re-run `paymentInfoChanged` so the parent's
   * `verifyPaymentInfo()` recognises that the cart now has a valid
   * `chargeProfile` (via the service's `addUpdateLocalItem` +
   * cart.setChargeProfile below) and can flip `paymentReady`
   * accordingly.
   */
  saveChargeProfileNow() {
    if (!this.canSaveChargeProfile) { return; }
    if (this.saveProfileState === 'SAVING') { return; }
    this.saveProfileState = 'SAVING';
    this.saveProfileErrorMessage = '';
    this.clearSaveProfileTimer();
    const cardNumber = (this.cart.cardNumberWithSpaces || '').split(' ').join('');
    const expMonth = Number((this.cart.expMonthYear || '').substring(0, 2));
    let expYear = Number((this.cart.expMonthYear || '').substring(3));
    if (expYear < 100) { expYear += 2000; }
    const useShipping = this.billingAddressTheSameAsShipping;
    const request: CreateChargeProfileRequest = {
      name: this.buildProfileNameWithLast4(cardNumber),
      firstName: this.cart.firstNamePayment,
      lastName: this.cart.lastNamePayment,
      cardNumber: cardNumber,
      expMonth: expMonth,
      expYear: expYear,
      csc: this.cart.csc,
      address1: useShipping ? this.cart.address.address1 : this.cart.address1Payment,
      address2: useShipping ? this.cart.address.address2 : this.cart.address2Payment,
      city: useShipping ? this.cart.address.city : this.cart.cityPayment,
      state: useShipping ? this.cart.address.state : this.cart.statePayment,
      postalCode: useShipping ? this.cart.address.postalcode : this.cart.postalCodePayment,
      country: useShipping ? this.cart.address.country : this.cart.countryPayment,
      setAsDefault: false
    };
    this.chargeProfileService.createChargeProfile(request).subscribe({
      next: profile => {
        if (profile && profile.ID > 0) {
          // Bind the freshly-saved profile as the cart's active
          // payment method so the user doesn't have to hunt for it
          // in the radio list after the form collapses.
          this.cart.setChargeProfile(profile);
          // Wipe raw card fields — we now have the token, and
          // holding on to the PAN/CSC in memory violates the
          // "round-trip exactly once" security rule from the
          // ChargeProfile contract.
          this.cart.cardNumber = '';
          this.cart.cardNumberWithSpaces = '';
          this.cart.csc = '';
          // Clear the "also save on place-order" flag — the card is
          // already saved, so the parent's `preparePaymentThenPlaceOrder`
          // would otherwise try to save it again as a duplicate on
          // submit.
          this.savePaymentInfoAsChargeProfile = false;
          this.savePaymentInfoAsChargeProfileChange.emit(false);
          this.savePaymentInfoAsChargeProfileName = '';
          this.savePaymentInfoAsChargeProfileNameChange.emit('');
          // Collapse the credit-card form so the saved-profile
          // radio list becomes visible with the new row selected.
          this.enterCreditCard = false;
          this.enterCreditCardChange.emit(false);
          this.saveProfileState = 'SAVED';
          this.saveProfileResetHandle = setTimeout(() => {
            this.saveProfileState = 'IDLE';
            this.saveProfileResetHandle = null;
          }, 3000);
          this.paymentInfoChanged.emit();
        } else {
          this.saveProfileState = 'ERROR';
          this.saveProfileErrorMessage = LanguagePipe.get('Could not save the credit card');
        }
      },
      error: err => {
        console.error('[cart-payment] saveChargeProfileNow failed', err);
        this.saveProfileState = 'ERROR';
        // Prefer the server-side message when the gateway returned
        // one (e.g. "Card declined"); fall back to a generic label.
        const serverMsg = err && err.error && err.error.message
            ? String(err.error.message)
            : (err && err.message ? String(err.message) : '');
        this.saveProfileErrorMessage = serverMsg
            ? `${LanguagePipe.get('Could not save the credit card')}: ${serverMsg}`
            : LanguagePipe.get('Could not save the credit card');
      }
    });
  }

  /** Cancel any pending SAVED → IDLE reset. */
  private clearSaveProfileTimer(): void {
    if (this.saveProfileResetHandle) {
      clearTimeout(this.saveProfileResetHandle);
      this.saveProfileResetHandle = null;
    }
  }

  // ─── Auto-open credit-card form ────────────────────────────────
  //
  // When the org has NO saved payment methods AND cannot use invoice
  // billing, the only way for the user to complete checkout is to
  // enter a card. Auto-opening the form saves the user one click and
  // makes the payment section immediately actionable instead of
  // showing just a lonely "Enter credit card information" button.
  //
  // We only auto-open ONCE per cart instance so a user who
  // explicitly closes the form (via Cancel) isn't fighting the
  // component to keep it collapsed.

  /**
   * Decide whether to auto-open the credit-card form on mount.
   * Fires once per cart instance (tracked by `autoOpenDecided`).
   * Requirements:
   *  - Have a cart.
   *  - Not already in `enterCreditCard` mode.
   *  - The invoice toggle can't be a fallback (cart is not
   *    `invoiceable`) — org that CAN invoice sees the choice offered
   *    below the payment area and shouldn't be forced into a form.
   *  - The saved-profile list must have finished loading AND be
   *    empty. If still loading, we retry on a short interval until
   *    the load resolves.
   */
  private maybeAutoOpenCreditCardForm(): void {
    if (this.autoOpenDecided) { return; }
    if (!this.cart) { return; }
    if (this.enterCreditCard) { this.autoOpenDecided = true; return; }
    if (this.cart.invoiceable) { this.autoOpenDecided = true; return; }
    const profiles = this.chargeProfileService.objects;
    const loaded = this.chargeProfileService.loadingState === 'LOADED';
    if (!loaded) {
      // The service is still fetching (or hasn't been asked to
      // fetch yet). Poll on a short interval and retry — cheap
      // because the section is only mounted while the user is
      // actively on the cart page. Cleared in `ngOnDestroy`.
      if (this.autoOpenPollHandle) { return; } // retry already scheduled
      this.autoOpenPollHandle = setTimeout(() => {
        this.autoOpenPollHandle = null;
        this.maybeAutoOpenCreditCardForm();
      }, 300);
      return;
    }
    // List is loaded — decide once.
    this.autoOpenDecided = true;
    if (!profiles || profiles.length === 0) {
      // No saved profiles + can't invoice → the user must enter a
      // card. Open the form so they can start typing immediately.
      this.enterCreditCard = true;
      this.enterCreditCardChange.emit(true);
    }
  }
}
